import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";

import useFetchData from "../hooks/useFetchData";
import { categorizeData, fetchMetricsData, getSvgMap } from "../utils/dashboardUtils";

import LegendMetricModal from "../components/LegendMetricModal";
import LoadingIndicator from "../components/LoadingIndicator";

import MetricCategoriesContext from "../context/MetricCategoriesContext";
import { handleExcelDownload } from "../utils/excelExportModule";
import DownloadLoading from "../components/DownloadLoading";

import downloadSvg from '../assets/download.svg';
import printSvg from '../assets/print.svg';
import mapSvg from '../assets/map.svg';
import MappableMetricsModal from "../components/mappableModal/MappableMetricsModal";
import LivabilityMetrics from "../components/panels/LivabilityMetrics";
import EnvironmentMetrics from "../components/panels/EnvironmentMetrics";
import EconomyMetrics from "../components/panels/EconomyMetrics";
import EducationMetrics from "../components/panels/EducationMetrics";
import AccessibilityMetrics from "../components/panels/AccessibilityMetrics";
import DemographicMetrics from "../components/panels/DemographicMetrics";
import { useMetadata } from "../context/MetadataContext";
import ClearMapSelectionModal from "../components/ClearMapSelectionModal";
import { DashboardColorContext } from "../context/DashboardColorContext";
import EducationMetricsSkeleton from "../components/skeletons/panels/EducationMetricsSkeleton";
import EconomicsMetricsSkeleton from "../components/skeletons/panels/EconomicsMetricsSkeleton";
import PeopleMetricsSkeleton from "../components/skeletons/panels/PeopleMetricsSkeleton";
import AccessibilityMetricsSkeleton from "../components/skeletons/panels/AccessibilityMetricsSkeleton";
import LivabilityMetricsSkeleton from "../components/skeletons/panels/LivabilityMetricsSkeleton";
import EnvironmentMetricsSkeleton from "../components/skeletons/panels/EnvironmentMetricsSkeleton";

// PDF Imports
import { createMetricsPDFDocument } from "./MetricsPDF";
import { announce } from "../utils/a11y";
import { pdf } from '@react-pdf/renderer';
import PDFLoading from "../components/pdf/PDFLoading";
import testLegend from '../assets/pngs/test_legend.png';
import testMap from '../assets/pngs/test_map.png';
import PopupBlockedBanner from "../components/accessibility/PopupBlockedBanner";
import { base64ToBlob } from "../utils/helpers";


const Dashboard = () => {
    const isIframe = window.self !== window.top;
    const navigate = useNavigate();
    const { tab } = useParams<{ tab: string }>();
    const { metricCategories } = useContext(MetricCategoriesContext);
    // Fetch metric metadata for the current org
    const metadata = useMetadata();

    const [currentTab, setCurrentTab] = useState<string>("");
    const [selectedArea, setSelectedArea] = useState('');
    const [legendId, setLegendId] = useState(0);

    const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
    const [isMappableModalOpen, setIsMappableModalOpen] = useState(false);
    const [shouldDownloadExcel, setShouldDownloadExcel] = useState(false);
    const [showDownloadLoading, setShowDownloadLoading] = useState(false);
    const [showPDFLoading, setShowPDFLoading] = useState(false);
    const [popupBlocked, setPopupBlocked] = useState(false);

    // Geographic levels and exclusion lists defined for excel (will vary by project)
    const geographicLevels = [
        ...new Set(metadata?.flatMap((metric) => 
          metric.versions.map((version) => version.level_of_detail)
        ))
    ];

    const summaryExclusionList: string[] = [];
    const disaggregationExclusionList: string[] = [];
    const geographyExclusionList: string[] = [];

    const { indicators, loading, dashboardView, geoDetails, geoTag, selectionActive, lodMissing } = useFetchData(currentTab || '');

    const hardCodedCategories = ['Demographics', 'Accessibility', 'Livability', 'Education', 'Economy', 'Environment'];
    const tabs = isIframe ? hardCodedCategories : metricCategories;

    // Set the current tab when not in Iframe view
    useEffect(() => {
        if (!isIframe && tabs.length > 0) {
            if (tab && tabs.includes(tab)) {
                setCurrentTab(tab.toLowerCase());
            } else if (!currentTab) {
                setCurrentTab(tabs[0]);
            }
        }
    }, [tab, isIframe, tabs]);

    useEffect(() => {
        if (isIframe && dashboardView && tabs.length > 0) {
            // If foundLayer is present, update the tabs
            if (!tabs.includes(dashboardView)) {
                const updatedTabs = [...tabs, dashboardView];
                setCurrentTab(updatedTabs.find((t) => t.toLowerCase() === dashboardView.toLowerCase()) || updatedTabs[0]);
            } else {
                setCurrentTab(tabs.find((t) => t.toLowerCase() === dashboardView.toLowerCase()) || tabs[0]);
            }
        } else {
            // When not in iframe, just ensure the currentTab is set based on the `tab` param
            setCurrentTab(tabs.find((t) => t.toLowerCase() === currentTab?.toLowerCase()) || tabs[0]);
        }
    }, [dashboardView, isIframe, tabs]);

    // Combine data from all sources
    const allDataAvailable =
        !loading && Boolean(indicators);

    const canRenderCurrentTab = Boolean(currentTab);

    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            if (event.data.message === 'pdf') {
                announce("Generating PDF. Please wait.");
                setShowPDFLoading(true);

                const mapUrl = event.data.params.url;
                const logo = event.data.params.logoUrl || '';
                const legend = event.data.params.legendUrl || '';
                const selection = event.data.params.selectedArea || '';
                // const site = event.data.params.siteUrl || '';
                setSelectedArea(selection);

                const imageBlob = base64ToBlob(mapUrl.split(',')[1], 'image/png');

                // Open a blank window synchronously (not blocked)
                const newWindow = window.open(
                    `${window.location.origin}/waiting`,
                    "_blank",
                );

                // If blocked, inform the user and stop
                if (!newWindow) {
                    console.warn('Popup blocked by browser');
                    setPopupBlocked(true);
                    announce('PDF popup was blocked. Please allow popups for this site and try again.');
                    setShowPDFLoading(false);
                    return;
                }
                
                try {
                    // Fetch the data first
                    const disaggregatedData = await fetchMetricsData(hardCodedCategories, geoDetails, geoTag);
                    console.log('disaggregated data for PDF:', disaggregatedData)

                    // Wait for all index classifications before creating the PDF.
                    const pdfDocument = await createMetricsPDFDocument({
                        logoUrl: logo,
                        legendUrl: legend,
                        selectedArea: selection,
                        pdfImageBlob: imageBlob,
                        disaggregatedData,
                        metadata,
                    });

                    const blob = await pdf(pdfDocument).toBlob();
                    const url = URL.createObjectURL(blob);

                    if (newWindow) {
                        newWindow.location.href = url;
                    }

                    announce('PDF is ready. The file has opened in a new tab.');
                } catch (err) {
                    console.error('Error generating PDF:', err);
                    announce('PDF generation failed. Please try again.');
                } finally {
                    setTimeout(() => setShowPDFLoading(false), 800);
                }
            } else if (event.data.message === 'metric-modal') {

                // Legend metadata info icon event
                console.log('Message received from parent app inside legend metric modal:', event.data);
                setLegendId(event.data.params.id);
            } else if (event.data.message === 'excel') {
                // Get selected area
                const sArea = event.data?.params?.selectedArea;
                // Show loading modal
                setShowDownloadLoading(true);
                
                if (!loading) {
                    // Execute excel download script
                    handleExcelDownload(geoDetails, geoTag, metricCategories, sArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'City of Lacey Excel Download').finally(() => {
                        setShowDownloadLoading(false);
                    });
                } else {
                    console.log('Data is still loading, will attempt to download Excel later.');
                    setShouldDownloadExcel(true); // Set flag to retry download
                }
            }
        };
    
        window.addEventListener('message', handleMessage);
    
        return () => {
          window.removeEventListener('message', handleMessage);
        };
    }, [allDataAvailable, geoDetails]);

    useEffect(() => {
        if (!loading && shouldDownloadExcel) {
            setShowDownloadLoading(true);
            handleExcelDownload(geoDetails, geoTag, metricCategories, selectedArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'City of Lacey Excel Download').finally(() => {
                setShouldDownloadExcel(false); 
                setShowDownloadLoading(false);
            });
        }
    }, [loading, shouldDownloadExcel, geoDetails]);

    const closeLegendModal = () => {
        setIsLegendModalOpen(false);
        setLegendId(-1);
    };

    const handleDownloadNoIframe = () => {
        // Show loading modal
        setShowDownloadLoading(true);
                
        if (!loading) {
            // Execute excel download script
            handleExcelDownload(geoDetails, geoTag, metricCategories, selectedArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'City of Lacey Excel Download').finally(() => {
                setShowDownloadLoading(false);
            });
        }
    };

    const handlePrintNoIframe = async () => {
        announce('Generating PDF. Please wait.');
        setShowPDFLoading(true);

        // Open a blank window synchronously (not blocked)
        const newWindow = window.open(
            `${window.location.origin}/waiting`,
            "_blank",
        );

        // If blocked, inform the user and stop
        if (!newWindow) {
            console.warn('Popup blocked by browser');
            setPopupBlocked(true);
            announce('PDF popup was blocked. Please allow popups for this site and try again.');
            setShowPDFLoading(false);
            return;
        }
        
        try {
            // Fetch the data first
            const disaggregatedData = await fetchMetricsData(hardCodedCategories, geoDetails, geoTag);
            console.log('disaggregated data for PDF:', disaggregatedData)

            // Wait for all index classifications before creating the PDF.
            const pdfDocument = await createMetricsPDFDocument({
                selectedArea,
                disaggregatedData,
                metadata,
                legendUrl: testLegend,
                pdfUrl: testMap
            });

            const blob = await pdf(pdfDocument).toBlob();
            const url = URL.createObjectURL(blob);

            if (newWindow) {
                newWindow.location.href = url;
            }

            announce('PDF is ready. The file has opened in a new tab.');
        } catch (err) {
            console.error('Error generating PDF:', err);
            announce('PDF generation failed. Please try again.');
        } finally {
            setTimeout(() => setShowPDFLoading(false), 800);
        }
    };

    const handleMappableModal = () => {
        setIsMappableModalOpen(true);
    }

    const closeMappableModal = () => {
        setIsMappableModalOpen(false);
    }

    const categorizedData = categorizeData(indicators);

    const renderMetrics = (tab: string) => {

        // Show modal to clear map selection if the current active selection is missing data or invalid (Small area problem)
        if (lodMissing) {
            return <ClearMapSelectionModal/>
        }

        /*
        * These states belong to indicator data. Programmatic Layers
        * manage their own loading and error states through their contexts.
        */
        if (loading) {
            if (tab.toLowerCase() === "education") {
                return <EducationMetricsSkeleton />;
            }

            if (tab.toLowerCase() === "economy") {
                return <EconomicsMetricsSkeleton />;
            }

            if (tab.toLowerCase() === "environment") {
                return <EnvironmentMetricsSkeleton />;
            }

            if (tab.toLowerCase() === "demographics") {
                return <PeopleMetricsSkeleton />;
            }

            if (tab.toLowerCase() === "accessibility") {
                return <AccessibilityMetricsSkeleton />;
            }

            if (tab.toLowerCase() === "livability") {
                return <LivabilityMetricsSkeleton />;
            }

            return <LoadingIndicator />;
        }

        const color = "#225591";
        console.log('tab:', tab);
    
        // Map tab names to corresponding components
        const metricsComponents: Record<string, JSX.Element> = {
            Livability: <LivabilityMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Economy: <EconomyMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Education: <EducationMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Environment: <EnvironmentMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Accessibility: <AccessibilityMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />,
            Demographics: <DemographicMetrics indicators={indicators} metadata={metadata} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag} selectionActive={selectionActive} />
        };
    
        // Return the corresponding component or null if the tab is not found
        return (
            <div className="h-full bg-[#F8F9FA]">
                <DashboardColorContext.Provider value={color}>
                    {metricsComponents[tab] || null}
                </DashboardColorContext.Provider>
            </div>
        );
    };

    // Set active tab to current tab if present
    const activeTab = currentTab?.toLowerCase() || tabs[0];

    return (
        <div className={`${!isIframe ? 'max-w-[456px] mx-auto print:max-w-full' : ''}`}>
            <div className="flex mr-4 items-center justify-end ">
            </div>
            {!isIframe && (
                <div className="my-2 mx-5 flex items-center justify-end gap-3">
                    <img src={printSvg} alt="print icon" title="Print PDF" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handlePrintNoIframe() }}/>
                    <img src={downloadSvg} alt="download icon" title="Download Excel" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleDownloadNoIframe() }}/>
                    <img src={mapSvg} alt="map icon" title="Show Mappable Metrics" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleMappableModal() }}/>
                </div>
            )}
           {!isIframe && <div className="flex m-4 items-center justify-center flex-wrap no-print">
                {tabs.map(tabName => (
                    <button
                        key={tabName}
                        className={`flex flex-col items-center justify-center w-32 h-8 p-1 rounded-md text-[10px] ${activeTab === tabName.toLowerCase() ? 'bg-color-cai-dashboard text-white' : 'bg-white text-color-cai-dashboard'}`}
                        onClick={() => {
                            navigate(`/dashboard/${tabName}`);
                            setCurrentTab(tabName);
                        }}
                    >
                        {tabName}
                    </button>
                ))}
            </div> }
            
            {/* Render metrics for current tab */}
            <div className="no-print">
                {
                    canRenderCurrentTab ? (
                        renderMetrics(currentTab)
                    ) : (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                        </div>
                    )
                }
            </div>

            <LegendMetricModal
                id={legendId}
                isOpen={isLegendModalOpen}
                onClose={closeLegendModal}
                geoDetails={geoDetails}
                svg={getSvgMap(legendId)}
                geoTag={geoTag}
                selectionActive={selectionActive}
            />

            {isMappableModalOpen && <MappableMetricsModal
                isOpen={isMappableModalOpen}
                onClose={closeMappableModal}
            />}

            {showDownloadLoading &&
                <DownloadLoading/>
            }

            {showPDFLoading && 
                <PDFLoading/>
            }

            {/* PDF Popup blocked */}
            <PopupBlockedBanner
                show={popupBlocked}
                onClose={() => setPopupBlocked(false)}
            />
        </div>
    );
}

export default Dashboard;